import { getApiBaseUrl } from "../api/baseUrl"
import { throwIfRateLimited } from "../api/rateLimit"
import type {
  SystemBattleBotsConfig,
  SystemBattleBotsSyncResponse,
} from "../schemas"

async function readSystemBattleBotsResponse(
  response: Response,
): Promise<SystemBattleBotsConfig | null> {
  throwIfRateLimited(response)

  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    const message = await response.text()
    throw new Error(message || "Failed to load system battle bots.")
  }

  const body = (await response.json()) as SystemBattleBotsSyncResponse
  return body.record
}

export async function fetchPublicSystemBattleBotsFromServer(
  signal?: AbortSignal,
): Promise<SystemBattleBotsConfig | null> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    return null
  }

  const response = await fetch(new URL("/api/system/battle-bots", baseUrl), {
    signal,
  })

  return readSystemBattleBotsResponse(response)
}

export async function fetchSystemBattleBotsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<SystemBattleBotsConfig | null> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    return null
  }

  const response = await fetch(
    new URL("/api/admin/system-battle-bots", baseUrl),
    {
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      signal,
    },
  )

  return readSystemBattleBotsResponse(response)
}

export async function syncSystemBattleBotsToServer(
  record: SystemBattleBotsConfig,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<SystemBattleBotsConfig> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new Error("Remote system battle bot sync is unavailable.")
  }

  const response = await fetch(
    new URL("/api/admin/system-battle-bots", baseUrl),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ record }),
      signal,
    },
  )

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new Error(message || "Failed to save system battle bots.")
  }

  const body = (await response.json()) as SystemBattleBotsSyncResponse
  return body.record
}
